import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, FileSpreadsheet, FileText, Download, Trash2, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import apiService from '@/services/api';

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);
const isExcel = (name) => /\.xlsx?$/i.test(name || "");

const ParseSummary = ({ summary }) => summary ? (
  <div className="text-xs text-slate-600 mt-1 space-y-1">
    <div>{summary.classes} classes · {summary.periods} periods · {summary.teachers_matched} teachers matched</div>
    {summary.unmatched_teachers?.length > 0 && (
      <div className="flex items-start gap-1 text-amber-700" data-testid="timetable-unmatched-teachers">
        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>Not matched to a portal account: {summary.unmatched_teachers.map(u => `${u.name} (${u.periods})`).join(", ")}</span>
      </div>
    )}
  </div>
) : null;

export const MasterTimetableUpload = ({ onImported, result, onResult }) => {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const setResult = onResult;
  const inputRef = useRef(null);

  const load = async () => {
    try { setFiles(await apiService.request('/api/timetable/master/files')); } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${apiService.baseURL}/api/timetable/master/upload`, { method: 'POST', body: fd, headers: { Authorization: `Bearer ${apiService.getToken()}` } });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || `Upload failed (${res.status})`);
      setResult(body);
      toast.success(body.parsed ? `Timetable imported — ${body.parsed.teachers_matched} teachers updated` : `${file.name} uploaded`);
      await load();
      if (body.parsed && onImported) onImported();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ''; }
  };

  const download = async (f) => {
    const res = await fetch(`${apiService.baseURL}/api/timetable/master/files/${f.id}/download`, { headers: { Authorization: `Bearer ${apiService.getToken()}` } });
    if (!res.ok) return toast.error('Download failed');
    const url = URL.createObjectURL(await res.blob());
    const a = Object.assign(document.createElement('a'), { href: url, download: f.filename }); a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete ${f.filename}? Teachers' timetables already imported will stay.`)) return;
    try { await apiService.request(`/api/timetable/master/files/${f.id}`, { method: 'DELETE' }); await load(); toast.success('File deleted'); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <Card data-testid="master-timetable-upload">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2"><Upload className="h-5 w-5 text-blue-600" /> Upload Master Timetable</CardTitle>
        <CardDescription>Excel files are read automatically — every teacher's "My Timetable" is rebuilt from it. PDF and Word files are kept here for reference. Only you, the principal and the academic principal can see these files; teachers see only their own periods.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <input ref={inputRef} type="file" accept=".xlsx,.xls,.pdf,.doc,.docx" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="master-timetable-file-input" />
          <Button onClick={() => inputRef.current?.click()} disabled={busy} data-testid="master-timetable-upload-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Choose file (Excel, PDF or Word)
          </Button>
          <span className="text-xs text-slate-500">Excel layout: TIME · DAY · one column per class; cells as "Subject" then teacher name on the next line.</span>
        </div>
        {result?.parsed && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="master-timetable-parse-result">
            <div className="flex items-center gap-2 font-medium"><CheckCircle2 className="h-4 w-4" /> Imported {result.filename}</div>
            <ParseSummary summary={result.parsed} />
          </div>
        )}
        {files.length > 0 && (
          <ul className="divide-y rounded-md border" data-testid="master-timetable-file-list">
            {files.map(f => (
              <li key={f.id} className="flex items-center gap-3 p-3" data-testid={`master-timetable-file-${f.id}`}>
                {isExcel(f.filename) ? <FileSpreadsheet className="h-5 w-5 text-emerald-600 shrink-0" /> : <FileText className="h-5 w-5 text-rose-600 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate">{f.filename}</div>
                  <div className="text-xs text-slate-500">{fmtSize(f.size_bytes)} · {f.uploaded_by_name || 'Unknown'} · {f.uploaded_at ? new Date(f.uploaded_at).toLocaleString() : ''}</div>
                  <ParseSummary summary={f.summary} />
                </div>
                {f.summary && <Badge variant="secondary">Imported</Badge>}
                <Button size="icon" variant="ghost" onClick={() => download(f)} data-testid={`master-timetable-download-${f.id}`}><Download className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove(f)} data-testid={`master-timetable-delete-${f.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
